import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { t } from '@lingui/core/macro';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Project } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ProjectCreationWaitModal from '../components/ProjectCreationWaitModal';
import BackButton from '../components/BackButton';
import { trackEvent, trackPageView } from '../statsig';
import './ProjectSelector.css';
import './SystemTemplateSelection.css';
import { SkeletonCard } from '../components/Skeleton';

export interface SystemTemplate {
  display_name_english: string;
  template_description: string;
  file_id: string;
  template_internal_key: string;
  production_type: string[];
  format_genre: string[];
  credit_type: string[];
  language: string[];
  territory: string[];
  broadcaster_platform: string[];
  fund_institution: string[];
  bilingual: boolean;
}

type FilterKey =
  | 'production_type'
  | 'format_genre'
  | 'credit_type'
  | 'language'
  | 'territory'
  | 'broadcaster_platform'
  | 'fund_institution';

type Filters = Record<FilterKey, string[]>;
type BilingualFilter = 'all' | 'yes' | 'no';
type MetadataFilterConfig = { key: FilterKey; label: string; order: number };
type BilingualFilterConfig = { key: 'bilingual'; label: string; order: number };
type FilterControlConfig = MetadataFilterConfig | BilingualFilterConfig;

interface SystemTemplateSelectionProps {
  onProjectSelect: (project: Project, isNewProject?: boolean) => void;
}

interface LocationState {
  projectName?: string;
}

const unorderedFilterConfigs: FilterControlConfig[] = [
  { key: 'production_type', label: 'Production type', order: 10 },
  { key: 'format_genre', label: 'Format / genre', order: 20 },
  { key: 'credit_type', label: 'Credit type', order: 30 },
  { key: 'territory', label: 'Territory', order: 40 },
  { key: 'broadcaster_platform', label: 'Broadcaster / platform', order: 50 },
  { key: 'fund_institution', label: 'Fund / institution', order: 60 },
  { key: 'language', label: 'Language', order: 70 },
  { key: 'bilingual', label: 'Bilingual', order: 80 },
];

const filterConfigs = [...unorderedFilterConfigs].sort((a, b) => a.order - b.order);

const isMetadataFilterConfig = (config: FilterControlConfig): config is MetadataFilterConfig =>
  config.key !== 'bilingual';

const metadataFilterConfigs = filterConfigs.filter(isMetadataFilterConfig);

const emptyFilters = (): Filters => ({
  production_type: [],
  format_genre: [],
  credit_type: [],
  language: [],
  territory: [],
  broadcaster_platform: [],
  fund_institution: [],
});

const includesAll = (values: string[], selectedValues: string[]): boolean =>
  selectedValues.length === 0 || selectedValues.every((selectedValue) => values.includes(selectedValue));

export function getSystemTemplateFilterOptions(
  templates: SystemTemplate[],
  key: FilterKey
): string[] {
  return Array.from(
    new Set(templates.flatMap((template) => template[key]).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
}

export function filterSystemTemplates(
  templates: SystemTemplate[],
  searchText: string,
  filters: Filters,
  bilingualFilter: BilingualFilter
): SystemTemplate[] {
  const normalizedSearch = searchText.trim().toLowerCase();
  return templates.filter((template) => {
    if (
      normalizedSearch &&
      !template.display_name_english.toLowerCase().includes(normalizedSearch)
    ) {
      return false;
    }

    if (
      bilingualFilter === 'yes' && !template.bilingual
    ) {
      return false;
    }
    if (
      bilingualFilter === 'no' && template.bilingual
    ) {
      return false;
    }

    return metadataFilterConfigs.every(({ key }) => includesAll(template[key], filters[key]));
  });
}

const fieldLabel: Record<FilterKey, string> = {
  production_type: 'Production',
  format_genre: 'Genre',
  credit_type: 'Credits',
  language: 'Language',
  territory: 'Territory',
  broadcaster_platform: 'Platform',
  fund_institution: 'Fund',
};

const SystemTemplateSelection: React.FC<SystemTemplateSelectionProps> = ({ onProjectSelect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProjectName = (location.state as LocationState | null)?.projectName ?? '';
  const [projectName, setProjectName] = useState<string>(initialProjectName);
  const [templates, setTemplates] = useState<SystemTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<SystemTemplate | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [filters, setFilters] = useState<Filters>(() => emptyFilters());
  const [bilingualFilter, setBilingualFilter] = useState<BilingualFilter>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [creatingProject, setCreatingProject] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleLogout = async (): Promise<void> => {
    await signOut(auth);
  };

  const fetchTemplates = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/system-templates`);
      const data = await response.json();
      if (data.status === 'OK') {
        setTemplates(data.templates ?? []);
      } else {
        setError(data.message || t`Failed to load system templates`);
      }
    } catch (err) {
      setError(t`Failed to load system templates. Please try again.`);
      console.error('Error fetching system templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackPageView('system_template_selection');
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = useMemo(
    () => filterSystemTemplates(templates, searchText, filters, bilingualFilter),
    [bilingualFilter, filters, searchText, templates]
  );

  const filterOptions = useMemo(() => {
    return metadataFilterConfigs.reduce<Record<FilterKey, string[]>>((acc, { key }) => {
      acc[key] = getSystemTemplateFilterOptions(templates, key);
      return acc;
    }, emptyFilters());
  }, [templates]);

  const handleCreateProject = async (): Promise<void> => {
    if (!projectName.trim() || !selectedTemplate) return;

    try {
      setCreatingProject(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: projectName.trim(),
          templateSpreadsheetId: selectedTemplate.file_id,
        }),
      });
      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_created', {
          project_id: data.project?.id,
          project_name: data.project?.name,
          source_type: 'system_template',
          template_internal_key: selectedTemplate.template_internal_key,
        });
        onProjectSelect(data.project, true);
        navigate('/app', { replace: true });
      } else {
        setError(data.message || t`Failed to create project`);
      }
    } catch (err) {
      setError(t`Failed to create project. Please try again.`);
      console.error('Error creating project from system template:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const renderTemplateMeta = (template: SystemTemplate): React.ReactNode => {
    const metaEntries = metadataFilterConfigs
      .map(({ key }) => ({ key, value: template[key].join(', ') }))
      .filter(({ value }) => value);
    return (
      <div className="system-template-meta">
        {metaEntries.map(({ key, value }) => (
          <span key={`${key}:${value}`} className="system-template-chip">
            <span>{fieldLabel[key]}</span>
            {value}
          </span>
        ))}
        <span className="system-template-chip">
          <span>{t`Bilingual`}</span>
          {template.bilingual ? t`Yes` : t`No`}
        </span>
      </div>
    );
  };

  return (
    <div className="project-selector system-template-page">
      <div className="project-selector-language">
        <LanguageSwitcher />
        <button
          type="button"
          className="language-menu-button"
          onClick={handleLogout}
          aria-label={t`Logout`}
          title={t`Logout`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            logout
          </span>
        </button>
      </div>

      <div className="project-selector-container system-template-container">
        <div className="system-template-header">
          <BackButton label={t`Back`} variant="ghost" />
          <div>
            <h1>{t`System Template Selection`}</h1>
            <p className="subtitle">{t`Choose the best starting point for your credits project`}</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="system-template-controls">
          <input
            type="search"
            placeholder={t`Search templates`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="project-name-input"
          />
        </div>

        <div className="system-template-filter-grid">
          {filterConfigs.map(({ key, label }) => (
            key === 'bilingual' ? (
              <div key={key} className="system-template-bilingual-filter" aria-label={t`Bilingual`}>
                {(['all', 'yes', 'no'] as BilingualFilter[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={bilingualFilter === value ? 'active' : ''}
                    onClick={() => setBilingualFilter(value)}
                  >
                    {value === 'all' ? t`All` : value === 'yes' ? t`Bilingual` : t`Not bilingual`}
                  </button>
                ))}
              </div>
            ) : (
              <Autocomplete
                key={key}
                multiple
                disablePortal
                options={filterOptions[key]}
                value={filters[key]}
                onChange={(_, value) => {
                  setFilters((prev) => ({ ...prev, [key]: value }));
                }}
                className="template-select system-template-filter"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={label}
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            )
          ))}
        </div>

        <div className="system-template-summary">
          {loading ? t`Loading system templates...` : t`${filteredTemplates.length} templates`}
        </div>

        <div className="system-template-list">
          {loading && Array.from({ length: 6 }, (_, index) => (
            <SkeletonCard key={index} dark />
          ))}
          {!loading && filteredTemplates.length === 0 && (
            <div className="no-projects">
              <p>{t`No templates match your filters.`}</p>
            </div>
          )}
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplate?.file_id === template.file_id;
            return (
              <button
                key={`${template.template_internal_key}:${template.file_id}`}
                type="button"
                className={`system-template-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedTemplate(template)}
              >
                <span className="system-template-card-title">{template.display_name_english}</span>
                {template.template_description && (
                  <span className="system-template-card-description">
                    {template.template_description}
                  </span>
                )}
                {renderTemplateMeta(template)}
              </button>
            );
          })}
        </div>

        <div className="system-template-create-bar">
          <div>
            <span className="system-template-create-label">{t`Selected template`}</span>
            <strong>{selectedTemplate?.display_name_english ?? t`None`}</strong>
          </div>
          <input
            type="text"
            placeholder={t`New project name`}
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            disabled={creatingProject}
            className="project-name-input system-template-project-name"
          />
          <span title={!projectName.trim() ? t`You must give a name to the project` : undefined}>
            <button
              type="button"
              className="create-button"
              disabled={creatingProject || !projectName.trim() || !selectedTemplate}
              onClick={handleCreateProject}
            >
              {creatingProject ? t`Creating...` : t`Create Project`}
            </button>
          </span>
        </div>
      </div>
      <ProjectCreationWaitModal open={creatingProject} />
    </div>
  );
};

export default SystemTemplateSelection;
